/**
 * Getypeerde toegang tot de compliance-routes: onder welke regels een woning valt,
 * hoe streng het platform daarop handelt, en wat er openstaat.
 *
 * Eén ding is hier geen implementatiedetail. **De keuze gaat als één PUT.** Gebied,
 * niveau, verklaring en erkenning worden samen vastgelegd, want er mag geen moment
 * bestaan waarop het niveau al verlaagd is en de verklaring nog niet is gegeven
 * (§17.15). Een client die de velden los zou kunnen sturen, maakt die toestand
 * alsnog mogelijk.
 */
import { call, isRecord, readText } from './http'
import type { Failure, Result } from './http'
import type { RequestOptions } from './api-client'

export type ComplianceFailure = Failure
export type ComplianceResult<T> = Result<T>

export const REGULATION_MODES = ['enforced', 'advisory', 'off'] as const
export type RegulationMode = (typeof REGULATION_MODES)[number]

export const COMPLIANCE_LEVELS = ['info', 'warning', 'required_action', 'block'] as const
export type ComplianceLevel = (typeof COMPLIANCE_LEVELS)[number]

export interface RegulationArea {
  readonly id: string
  readonly code: string
  readonly kind: string
  readonly name: string
  readonly parentId: string | null
}

export interface RegulationChoice {
  readonly areaId: string | null
  readonly suggestedAreaId: string | null
  readonly mode: RegulationMode | null
  readonly declaration: string
  readonly registrationNumber: string
  readonly decidedAt: string | null
  readonly disclaimerVersion: string
  readonly disclaimerAcceptedAt: string | null
  /** De versie die het platform nú vraagt; wijkt hij af, dan moet er opnieuw erkend worden. */
  readonly currentDisclaimerVersion: string
}

export interface ComplianceFinding {
  readonly id: string
  readonly type: string
  readonly level: ComplianceLevel
  readonly windowFrom: string | null
  readonly windowTo: string | null
  readonly limitNights: number | null
  readonly nightsUsed: number | null
  readonly status: string
  /** Uit de regelteksten in de database, in de gevraagde taal (§17.8). */
  readonly title: string
  readonly summary: string
  readonly actionText: string
  readonly sourceUrl: string | null
  readonly sourceCitation: string | null
  readonly lastCheckedOn: string | null
}

export interface ComplianceOverview {
  readonly choice: RegulationChoice
  readonly findings: readonly ComplianceFinding[]
  readonly blocked: boolean
}

function isOneOf<T extends string>(value: unknown, known: readonly T[]): value is T {
  return typeof value === 'string' && (known as readonly string[]).includes(value)
}

function readNullableText(source: Record<string, unknown>, field: string): string | null {
  const raw = source[field]
  return typeof raw === 'string' && raw !== '' ? raw : null
}

function readNullableCount(source: Record<string, unknown>, field: string): number | null {
  const raw = source[field]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

function readChoice(value: unknown): RegulationChoice | null {
  if (!isRecord(value)) {
    return null
  }
  const mode = value['mode']
  return {
    areaId: readNullableText(value, 'areaId'),
    suggestedAreaId: readNullableText(value, 'suggestedAreaId'),
    mode: isOneOf(mode, REGULATION_MODES) ? mode : null,
    declaration: readText(value, 'declaration'),
    registrationNumber: readText(value, 'registrationNumber'),
    decidedAt: readNullableText(value, 'decidedAt'),
    disclaimerVersion: readText(value, 'disclaimerVersion'),
    disclaimerAcceptedAt: readNullableText(value, 'disclaimerAcceptedAt'),
    currentDisclaimerVersion: readText(value, 'currentDisclaimerVersion'),
  }
}

function readFinding(value: unknown): ComplianceFinding | null {
  if (!isRecord(value)) {
    return null
  }
  const id = readText(value, 'id')
  const level = value['level']
  const type = readText(value, 'type')
  // Een bevinding met een onbekend niveau wordt overgeslagen: hem als 'info' tonen
  // zou een blokkade kunnen verbergen, en dat is de ene fout die hier niet mag.
  if (id === '' || type === '' || !isOneOf(level, COMPLIANCE_LEVELS)) {
    return null
  }
  return {
    id,
    type,
    level,
    windowFrom: readNullableText(value, 'windowFrom'),
    windowTo: readNullableText(value, 'windowTo'),
    limitNights: readNullableCount(value, 'limitNights'),
    nightsUsed: readNullableCount(value, 'nightsUsed'),
    status: readText(value, 'status'),
    title: readText(value, 'title'),
    summary: readText(value, 'summary'),
    actionText: readText(value, 'actionText'),
    sourceUrl: readNullableText(value, 'sourceUrl'),
    sourceCitation: readNullableText(value, 'sourceCitation'),
    lastCheckedOn: readNullableText(value, 'lastCheckedOn'),
  }
}

export async function listAreas(
  locale: string,
  options: RequestOptions = {},
): Promise<ComplianceResult<readonly RegulationArea[]>> {
  return call(
    'GET',
    `/regulation-areas?locale=${encodeURIComponent(locale)}`,
    undefined,
    (payload) => {
      if (!isRecord(payload) || !Array.isArray(payload['areas'])) {
        return null
      }
      const areas: RegulationArea[] = []
      for (const entry of payload['areas']) {
        if (!isRecord(entry)) {
          continue
        }
        const id = readText(entry, 'id')
        if (id === '') {
          continue
        }
        areas.push({
          id,
          code: readText(entry, 'code'),
          kind: readText(entry, 'kind'),
          name: readText(entry, 'name'),
          parentId: readNullableText(entry, 'parentId'),
        })
      }
      return areas
    },
    options,
  )
}

export async function fetchCompliance(
  residenceId: string,
  locale: string,
  options: RequestOptions = {},
): Promise<ComplianceResult<ComplianceOverview>> {
  return call(
    'GET',
    `/residences/${encodeURIComponent(residenceId)}/compliance?locale=${encodeURIComponent(locale)}`,
    undefined,
    (payload) => {
      if (!isRecord(payload)) {
        return null
      }
      const choice = readChoice(payload['choice'])
      if (choice === null) {
        return null
      }
      const findings: ComplianceFinding[] = []
      if (Array.isArray(payload['findings'])) {
        for (const entry of payload['findings']) {
          const finding = readFinding(entry)
          if (finding !== null) {
            findings.push(finding)
          }
        }
      }
      return { choice, findings, blocked: payload['blocked'] === true }
    },
    options,
  )
}

export interface ChoiceInput {
  readonly areaId: string | null
  readonly mode: RegulationMode
  readonly declaration: string
  readonly registrationNumber: string
  readonly acceptDisclaimer: boolean
}

export async function saveChoice(
  residenceId: string,
  input: ChoiceInput,
): Promise<ComplianceResult<RegulationChoice>> {
  return call(
    'PUT',
    `/residences/${encodeURIComponent(residenceId)}/compliance`,
    input,
    (payload) => (isRecord(payload) ? readChoice(payload['choice']) : null),
  )
}

export async function acknowledgeFinding(findingId: string): Promise<ComplianceResult<true>> {
  return call(
    'POST',
    `/compliance/findings/${encodeURIComponent(findingId)}/acknowledge`,
    undefined,
    () => true,
  )
}

/** Hoeveel nachten er nog over zijn; `null` wanneer er geen limiet in het spel is. */
export function nightsRemaining(finding: ComplianceFinding): number | null {
  if (finding.limitNights === null || finding.nightsUsed === null) {
    return null
  }
  return Math.max(0, finding.limitNights - finding.nightsUsed)
}

export function complianceErrorMessageKey(failure: ComplianceFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.compliance.error.rejected'
    case 'not_found':
      return 'dashboard.compliance.error.notFound'
    case 'forbidden':
      return 'dashboard.compliance.error.forbidden'
    default:
      return 'dashboard.compliance.error.unavailable'
  }
}
